import type { ReactNode } from 'react'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { SuspenseFallback } from '@fourthwall/components'

import { useCreateDraftMutation } from '../hooks/useCreateDraftMutation'
import { useCurrentShopContext } from '../hooks/useCurrentShopContext'
import { useActiveThemeContext } from './ActiveThemeProvider'

type Props = {
  children: ReactNode
}

export type DraftContextType = {
  draftId: string
}

export const DraftContext = createContext<DraftContextType | undefined>(undefined)

export const DraftProvider = ({ children }: Props) => {
  const { currentShop } = useCurrentShopContext()
  const { activeTheme } = useActiveThemeContext()
  const { createDraftMutation } = useCreateDraftMutation()

  const [draftId, setDraftId] = useState<string>('')

  useEffect(() => {
    createDraftMutation.mutate(
      [
        { shop_id: currentShop.id },
        {
          shop_theme_id: activeTheme.id,
        },
      ],
      {
        onSuccess: ({ id }) => {
          setDraftId(id)
        },
      },
    )
  }, [activeTheme])

  const value = useMemo(() => ({ draftId }), [draftId])

  if (!draftId || draftId === '') {
    return <SuspenseFallback />
  }

  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>
}

export const useDraftContext = () => {
  const draftContext = useContext(DraftContext)

  if (draftContext === undefined) {
    throw new Error('draftContext must be used within a DraftProvider')
  }

  return draftContext
}
